import React, { useState } from "react";
import { Button, Icon, Modal, StatusBadge, Text } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  ESTABLISHMENT_STATUS,
  USER_ROLE,
} from "../../../common/model/enum";
import { useAdmin } from "../../../hooks/useAdmin";
import { useOperation } from "../../../hooks/useOperation";
import { useEstablishment } from "../hooks/useEstablishment";
import PartnerType from "../partnerType/PartnerType";

import styles from "./EstablishmentTable.module.css";

const mapEstablishmentStatusToBadge = (status) => {
  if (status === ESTABLISHMENT_STATUS.ACTIVATED) {
    return { label: "Actif", type: "active", iconName: "iconCheck" };
  }

  if (status === ESTABLISHMENT_STATUS.MISSING_INFORMATIONS) {
    return { label: "Incomplet", type: "pending", iconName: "iconWarning" };
  }

  if (status === ESTABLISHMENT_STATUS.WAITING_FOR_APPROVAL) {
    return { label: "En attente d'approbation", type: "pending", iconName: "iconTimer" };
  }

  if (status === ESTABLISHMENT_STATUS.DISABLED) {
    return { label: "Désactivé", type: "inactive", iconName: "iconPlugs" };
  }

  return { label: "Inactif", type: "inactive", iconName: "iconPlugs" };
};

const EstablishmentTable = ({ establishments, onOpenEstablishment, onDeleted }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization } = useAdmin();
  const { showToast } = useOperation();
  const { removeEstablishment, loading: deleteLoading } = useEstablishment(
    organization?.entityId
  );
  const [selectedToDelete, setSelectedToDelete] = useState(null);

  const closeDeleteModal = () => setSelectedToDelete(null);

  const onDelete = async () => {
    if (!selectedToDelete?.id) return;

    try {
      const response = await removeEstablishment(selectedToDelete.id);
      const isDeleted = Boolean(response?.data?.MT_DELETE_ESTABLISHMENT);

      if (!isDeleted) {
        showToast("La suppression de l'établissement a échoué.", "error");
        return;
      }

      closeDeleteModal();
      showToast("Établissement supprimé avec succès.", "success");
      // La page affichée est relue (la liste centrale l'est via useEstablishment).
      onDeleted?.();
    } catch (error) {
      showToast("La suppression de l'établissement a échoué.", "error");
      navigate("/error");
    }
  };

  if (!establishments.length) {
    return (
      <div className={styles.emptyState}>
        <Text size="sm" color="muted">
          Aucun établissement à afficher.
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th>Nom</th>
            <th>Type</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {establishments.map((item) => {
            const status = mapEstablishmentStatusToBadge(item.status);

            return (
              <tr
                key={item.id}
                className={styles.clickableRow}
                onClick={() => onOpenEstablishment(item.id)}
              >
                <td>
                  <button
                    type="button"
                    className={styles.nameLink}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenEstablishment(item.id);
                    }}
                  >
                    {item.company?.legalName}
                  </button>
                </td>
                <td>
                  <PartnerType partnerType={item.activities} />
                </td>
                <td>
                  <StatusBadge
                    iconName={status.iconName}
                    iconPosition="left"
                    label={status.label}
                    type={status.type}
                    size="small"
                  />
                </td>
                <td>
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label="Voir l'établissement"
                      title="Voir"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenEstablishment(item.id);
                      }}
                    >
                      <Icon name="iconEye" size="sm" />
                    </button>

                    {item?.userRole === USER_ROLE.PA && (
                      <button
                        type="button"
                        className={styles.iconBtn}
                        aria-label="Modifier l'établissement"
                        title="Modifier"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`${item.id}/edit`);
                        }}
                      >
                        <Icon name="iconPenToSquare" size="sm" />
                      </button>
                    )}

                    {item?.userRole === USER_ROLE.PA && (
                      <button
                        type="button"
                        className={styles.iconBtn}
                        aria-label="Supprimer l'établissement"
                        title="Supprimer"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedToDelete(item);
                        }}
                      >
                        <Icon name="iconTrash" size="sm" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Modal t={t}
        isOpen={Boolean(selectedToDelete)}
        onClose={closeDeleteModal}
        title={t("manageEstablishment.modal.deleteEstablishment")}
        footer={
          <div className={styles.modalFooter}>
            <Button
              label={t("commons.buttons.cancel")}
              variant="secondary"
              onClick={closeDeleteModal}
            />
            <Button
              label={t("commons.buttons.submit")}
              onClick={onDelete}
              disabled={deleteLoading}
            />
          </div>
        }
      >
        <Text size="sm">{t("manageEstablishment.modal.deletePopup")}</Text>
      </Modal>
    </div>
  );
};

export default EstablishmentTable;
